import { useMenuImport } from "@/hooks/importers";

const DOORDASH_URL_PATTERN = /doordash\.com/i;
const UBER_EATS_URL_PATTERN = /ubereats\.com/i;

export const MENU_IMPORT_ALLOWED_ORDERING_HOSTS = [
  "api.orderingplus.com",
  "apiv4.ordering.co",
  "api.ordering.co",
];

const LITERAL_IP = /^(?:\d{1,3}\.){3}\d{1,3}$/;

const validateDoorDashUrl = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return { valid: false, error: "validation.urlRequired" };
  if (!DOORDASH_URL_PATTERN.test(trimmed))
    return { valid: false, error: "validation.invalidUrl" };
  return { valid: true };
};

const validateUberEatsUrl = (url) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return { valid: false, error: "validation.urlRequired" };
  if (!UBER_EATS_URL_PATTERN.test(trimmed))
    return { valid: false, error: "validation.invalidUrlUbereats" };
  return { valid: true };
};

const validateDoorDashForm = (form) => validateDoorDashUrl(form.url);

const validateUberEatsForm = (form) => validateUberEatsUrl(form.url);

/** OrderingPlus business API URL: path must include /business/{segment}. */
const hostnameOf = (rawUrl) => {
  try {
    return new URL(rawUrl).hostname.toLowerCase();
  } catch {
    return "";
  }
};

const isBlockedOrderingHost = (host) =>
  !host || host === "localhost" || host.includes(":") || LITERAL_IP.test(host);

const orderingHostAllowed = (rawUrl, projectApiRoot) => {
  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  if (parsed.port && parsed.port !== "443") return false;
  const host = parsed.hostname.toLowerCase();
  if (isBlockedOrderingHost(host)) return false;
  const allowed = new Set(
    MENU_IMPORT_ALLOWED_ORDERING_HOSTS.map((item) => item.toLowerCase()),
  );
  const projectHost = hostnameOf(projectApiRoot);
  if (projectHost && !isBlockedOrderingHost(projectHost)) {
    allowed.add(projectHost);
  }
  return allowed.has(host);
};

const validateOrderingBusinessUrl = (rawUrl, projectApiRoot) => {
  const trimmed = (rawUrl || "").trim();
  if (!trimmed) return { valid: false, error: "validation.urlRequired" };
  try {
    const u = new URL(trimmed);
    const marker = "/business/";
    const idx = u.pathname.indexOf(marker);
    if (idx === -1)
      return { valid: false, error: "validation.orderingInvalidUrl" };
    const after = u.pathname.slice(idx + marker.length).replace(/\/$/, "");
    const segment = after.split("/").filter(Boolean)[0];
    if (!segment)
      return { valid: false, error: "validation.orderingInvalidUrl" };
    if (!orderingHostAllowed(trimmed, projectApiRoot)) {
      return { valid: false, error: "validation.orderingHostNotAllowed" };
    }
    return { valid: true };
  } catch {
    return { valid: false, error: "validation.orderingInvalidUrl" };
  }
};

const validateOrderingForm = (form, context = {}) => {
  const url = (form.url || "").trim();
  const projectId = (form.orderingPlusProjectId || "").trim();
  const segment = (form.orderingBusinessSegment || "").trim();

  if (url) return validateOrderingBusinessUrl(url, context.projectApiRoot);
  if (projectId && segment) return { valid: true };
  if (projectId || segment)
    return { valid: false, error: "validation.orderingIncompleteStructured" };
  return { valid: false, error: "validation.orderingSourceRequired" };
};

const validateGloriaFoodKey = (value, requiredError) => {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    return { valid: false, error: requiredError };
  }
  if (trimmed.length > 128 || /\s/.test(trimmed)) {
    return { valid: false, error: "validation.invalidGloriafoodKey" };
  }
  return { valid: true };
};

/** Restaurant Key in `url`, Master Key in `tracker` (shared modal field slots). */
const validateGloriaFoodForm = (form) => {
  const restaurantKey = validateGloriaFoodKey(
    form.url,
    "validation.gloriafoodRestaurantKeyRequired",
  );
  if (!restaurantKey.valid) return restaurantKey;
  return validateGloriaFoodKey(
    form.tracker,
    "validation.gloriafoodMasterKeyRequired",
  );
};

/**
 * Menu importer cards configuration.
 * Add new importers (Uber Eats, Grubhub, etc.) with card config + modalConfig.
 */
export const IMPORTER_CARDS = [
  {
    id: "doordash",
    searchTerms: ["doordash"],
    titleKey: "doordash.cardTitle",
    descriptionKey: "doordash.cardDescription",
    importButtonKey: "doordash.importButton",
    logoColor: "#FF3008",
    modalConfig: {
      titleKey: "modal.title",
      subtitleKey: "modal.subtitle",
      urlLabelKey: "modal.urlLabel",
      urlPlaceholderKey: "modal.urlPlaceholder",
      urlHelperKey: "modal.urlHelper",
      statusImportingKey: "status.importing",
      includeAllMenuVariantsKey: "modal.includeAllMenuVariants",
      validateForm: validateDoorDashForm,
      useImport: () => useMenuImport("doordash"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants = true,
        userEmail = null,
      ) => ({
        doordashUrl: (form.url || "").trim(),
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants,
        userEmail: userEmail || undefined,
        useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
      }),
    },
  },
  {
    id: "ubereats",
    searchTerms: ["uber", "eats", "ubereats"],
    titleKey: "ubereats.cardTitle",
    descriptionKey: "ubereats.cardDescription",
    importButtonKey: "ubereats.importButton",
    logoColor: "#06C167",
    modalConfig: {
      titleKey: "modal.ubereatsTitle",
      subtitleKey: "modal.ubereatsSubtitle",
      urlLabelKey: "modal.ubereatsUrlLabel",
      urlPlaceholderKey: "modal.ubereatsUrlPlaceholder",
      urlHelperKey: "modal.ubereatsUrlHelper",
      statusImportingKey: "status.importingUbereats",
      includeAllMenuVariantsKey: "modal.includeAllMenuVariants",
      validateForm: validateUberEatsForm,
      useImport: () => useMenuImport("ubereats"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants = true,
        userEmail = null,
      ) => ({
        url: (form.url || "").trim(),
        businessIds,
        project,
        orderingApiBaseUrl,
        includeAllMenuVariants,
        userEmail: userEmail || undefined,
        useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
      }),
    },
  },
  {
    id: "ordering",
    searchTerms: ["ordering", "orderingplus", "ordering.co", "orderingco"],
    titleKey: "ordering.cardTitle",
    descriptionKey: "ordering.cardDescription",
    importButtonKey: "ordering.importButton",
    logoColor: "#7C3AED",
    modalConfig: {
      titleKey: "modal.orderingTitle",
      subtitleKey: "modal.orderingSubtitle",
      urlLabelKey: "modal.orderingUrlLabel",
      urlPlaceholderKey: "modal.orderingUrlPlaceholder",
      urlHelperKey: "modal.orderingUrlHelper",
      orderingProjectLabelKey: "modal.orderingProjectLabel",
      orderingProjectPlaceholderKey: "modal.orderingProjectPlaceholder",
      orderingBusinessSegmentLabelKey: "modal.orderingBusinessSegmentLabel",
      orderingBusinessSegmentPlaceholderKey:
        "modal.orderingBusinessSegmentPlaceholder",
      orderingStructuredHintKey: "modal.orderingStructuredHint",
      statusImportingKey: "status.importingOrdering",
      includeAllMenuVariantsKey: null,
      validateForm: validateOrderingForm,
      useImport: () => useMenuImport("ordering"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        _includeAllMenuVariants,
        userEmail = null,
      ) => {
        const url = (form.url || "").trim();
        const base = {
          businessIds,
          project,
          orderingApiBaseUrl,
          userEmail: userEmail || undefined,
          useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
        };
        if (url) {
          return { ...base, url };
        }
        return {
          ...base,
          orderingPlusProjectId:
            (form.orderingPlusProjectId || "").trim() || (project || "").trim(),
          orderingBusinessSegment: (form.orderingBusinessSegment || "").trim(),
        };
      },
    },
  },
  {
    id: "gloriafood",
    searchTerms: ["gloriafood", "gloria", "onlineorderpro", "globalfood"],
    titleKey: "gloriafood.cardTitle",
    descriptionKey: "gloriafood.cardDescription",
    importButtonKey: "gloriafood.importButton",
    logoColor: "#F15A24",
    modalConfig: {
      titleKey: "modal.gloriafoodTitle",
      subtitleKey: "modal.gloriafoodSubtitle",
      urlLabelKey: "modal.gloriafoodRestaurantKeyLabel",
      urlPlaceholderKey: "modal.gloriafoodRestaurantKeyPlaceholder",
      urlHelperKey: "modal.gloriafoodRestaurantKeyHelper",
      trackerLabelKey: "modal.gloriafoodMasterKeyLabel",
      trackerPlaceholderKey: "modal.gloriafoodMasterKeyPlaceholder",
      trackerHelperKey: "modal.gloriafoodMasterKeyHelper",
      statusImportingKey: "status.importingGloriafood",
      includeAllMenuVariantsKey: null,
      validateForm: validateGloriaFoodForm,
      useImport: () => useMenuImport("gloriafood"),
      buildImportParams: (
        form,
        businessIds,
        project,
        orderingApiBaseUrl,
        _includeAllMenuVariants,
        userEmail = null,
      ) => ({
        gloriafoodRestaurantKey: (form.url || "").trim(),
        businessIds,
        project,
        orderingApiBaseUrl,
        userEmail: userEmail || undefined,
        useFixture: import.meta.env.VITE_MENU_IMPORT_USE_FIXTURE === "true",
      }),
    },
  },
];
